/* eslint-disable react/prop-types */
import React, { useRef, useState, Fragment } from 'react';
import PropTypes from 'prop-types';

import './map.scss';
import { TYPES } from 'envs';
import Tooltip from 'components/tooltip';
import { isLaSexta, isAntena3, getSite, customColor } from '../../constants';

const CustomG = ({
  mapElement,
  currentCommunity,
  selectedPlace,
  mouseEnter,
  mouseLeave,
  getWinner,
  clickElement,
  showDots = false,
  excludedGrandParentIds,
  electionType,
  onlyPathProvince
}) => {
  const isExcluded = mapEl =>
    excludedGrandParentIds.some(data => data === mapEl.parentId);

  const winnerResult = customColor(getWinner(mapElement, showDots), getSite());

  let href = mapElement.path;
  if (!showDots && !currentCommunity) {
    href = mapElement.community.path;
  }
  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      href={isExcluded(mapElement) ? '#' : `${href}`}
      target="_parent"
      onClick={e => clickElement(e, mapElement)}
      style={{ pointerEvents: isExcluded(mapElement) ? 'none' : 'auto' }}
      title={mapElement.name}
      rel="noreferrer"
    >
      <g
        className={`${mapElement.name} ${mapElement.ineId}`}
        data-hover={
          currentCommunity
            ? mapElement.parentId === currentCommunity
            : undefined
        }
        data-selected={
          mapElement.parentId === selectedPlace.ineId ||
          selectedPlace.ineId === mapElement.ineId
        }
        data-sibiling={mapElement.parentId === selectedPlace.parentId}
        onMouseMove={e =>
          mouseEnter(e, {
            ...mapElement,
            ...getWinner(mapElement)
          })
        }
        onMouseLeave={e =>
          mouseLeave(e, {
            ...mapElement,
            ...getWinner(mapElement)
          })
        }
      >
        {onlyPathProvince && (
          <path
            key={mapElement.ineId}
            className={`${
              electionType === TYPES.MUNICIPAL
                ? 'mapElementMunicipal'
                : 'mapElement'
            } ${mapElement.name} ${mapElement.ineId}`}
            fill={
              isExcluded(mapElement)
                ? '#D9D9D9'
                : !showDots
                ? winnerResult
                : '#D9D9D9'
            }
            onClick={e => clickElement(e, mapElement)}
            d={mapElement.svg}
          />
        )}
        {showDots && !onlyPathProvince && (
          <circle
            xmlns="http://www.w3.org/2000/svg"
            className="st1"
            cx={mapElement.svgDots.x}
            cy={mapElement.svgDots.y}
            r="7"
            fill={winnerResult || '#f00'}
          />
        )}
      </g>
    </a>
  );
};

const SvgMap = ({
  allMap,
  onClick,
  mapElements,
  className,
  viewVoxStyle,
  partiesWinners,
  partiesWinnersNoFlat,
  currentCommunity,
  selectedPlace,
  showDots = false,
  excludedGrandParentIds = [],
  electionType = 'REGIONAL'
}) => {
  const svgEl = useRef(null);
  const pathEl = useRef(null);
  const [tooltipData, setTooltipData] = useState(null);

  const isExcluded = mapElement =>
    excludedGrandParentIds.some(data => data === mapElement.parentId);

  const clickElement = (event, mapElement) => {
    if (!isExcluded(mapElement)) {
      onClick(mapElement, svgEl, event);
    }
  };

  const getWinner = (mapElement, showDot) => {
    let win = { partyColor: '#D9D9D9' };
    if (partiesWinners && partiesWinners.find && partiesWinners.length) {
      win = partiesWinners.find(winner => {
        if (allMap && electionType === 'REGIONAL') {
          return winner.placeId === mapElement.parentId;
        }
        return (
          (showDot ? winner.placeId.substring(0, 2) : winner.placeId) ===
          mapElement.ineId
        );
      }) || {
        partyColor: '#D9D9D9',
        empty: true
      };
    }
    if (win.empty && !win.seats && !win.votes) {
      win.partyColor = '#A4a4a4';
      win.laSextaColor = '#A4a4a4';
      win.a3Color = '#A4a4a4';
      win.laRazonColor = '#A4a4a4';
    }

    if (win.partyColor && !win.partyColor.includes('#')) {
      win.partyColor = `#${win.partyColor}`;
    }

    return win;
  };

  const mouseChange = (e, place) => {
    if (TYPES.MUNICIPAL === electionType) {
      if (!place || !partiesWinners || !partiesWinners.length) {
        setTooltipData(null);
        return;
      }
      const pos = { x: e.clientX, y: e.clientY };
      const prov = partiesWinners.find(p => p.placeId === place.capital.ineId);
      const data = prov.partiesResult;
      setTooltipData({ pos, place: place ? place.capital : null, data });
    } else {
      const pos = { x: e.clientX, y: e.clientY };
      if (allMap) {
        const data = JSON.parse(
          JSON.stringify(partiesWinnersNoFlat[place?.community?.ineId] || [])
        ).reduce((total, prov) => {
          if (prov.placeId === place?.community?.ineId) {
            return total;
          }
          if (!prov.partiesResult) {
            return total;
          }
          prov.partiesResult.forEach(result => {
            const index = total.findIndex(tt => tt.id === result.id);
            if (index === -1) {
              total.push(result);
            } else {
              // eslint-disable-next-line no-param-reassign
              total[index].seats += result.seats;
            }
          });
          return total;
        }, []);
        setTooltipData({ pos, place: place ? place.community : null, data });
      } else {
        const data = (partiesWinnersNoFlat[place?.community?.ineId] || []).find(
          p => p.placeId === place.ineId
        );
        setTooltipData({ pos, place, data: data?.partiesResult });
      }
    }
  };

  const mouseLeave = e => {
    mouseChange(e, null);
  };

  const mouseEnter = (e, mapElement) => {
    mouseChange(e, mapElement);
  };

  return (
    <Fragment>
      <svg
        ref={svgEl}
        className={`map ${className}`}
        viewBox="0 0 511.8 393.8"
        xmlSpace="preserve"
        data-current={currentCommunity}
        data-isselected={currentCommunity !== undefined}
      >
        <g
          ref={pathEl}
          className="svgContainer"
          style={viewVoxStyle}
          fill="none"
        >
          {mapElements &&
            mapElements.map(mapElement => (
              <CustomG
                key={`${mapElement.ineId}-${mapElement.name}`}
                mapElement={mapElement}
                currentCommunity={currentCommunity}
                selectedPlace={selectedPlace}
                mouseEnter={mouseEnter}
                mouseLeave={mouseLeave}
                getWinner={getWinner}
                showDots={showDots}
                clickElement={clickElement}
                excludedGrandParentIds={excludedGrandParentIds}
                electionType={electionType}
                onlyPathProvince
              />
            ))}
          {mapElements &&
            showDots &&
            mapElements.map(mapElement => (
              <CustomG
                key={`${mapElement.ineId}-${mapElement.name}`}
                mapElement={mapElement}
                currentCommunity={currentCommunity}
                selectedPlace={selectedPlace}
                mouseEnter={mouseEnter}
                mouseLeave={mouseLeave}
                getWinner={getWinner}
                showDots={showDots}
                clickElement={clickElement}
                excludedGrandParentIds={excludedGrandParentIds}
                electionType={electionType}
                onlyPathProvince={false}
              />
            ))}
        </g>
      </svg>
      {tooltipData && tooltipData.pos.x && tooltipData.place && (
        <Tooltip
          mousePos={tooltipData.pos}
          data={tooltipData.data}
          selectedItem={tooltipData.place}
          showTooltipInfo={!!tooltipData}
        />
      )}
    </Fragment>
  );
};

SvgMap.propTypes = {
  onClick: PropTypes.func.isRequired,
  mapElements: PropTypes.arrayOf(PropTypes.shape()),
  className: PropTypes.string
};

SvgMap.defaultProps = {
  className: '',
  mapElements: []
};

export default SvgMap;
